import { ChevronDown, KeyRound, LogOut, MonitorSmartphone } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';

import { useCurrentUser } from '@shared/session/use-session';
import { useSessionStore } from '@shared/session/session.store';
import { cn } from '@shared/ui/cn';

/**
 * Menu de la persona que esta dentro.
 *
 * Existe por una razon muy concreta: hasta ahora no habia forma de salir. El
 * caso de uso, la llamada del cliente y el endpoint del servidor estaban los
 * tres escritos, y ningun componente los invocaba. Se podia "salir" recargando
 * -el access token vive solo en memoria- pero eso deja el refresh token vivo
 * en el servidor hasta que caduca, que es exactamente lo que cerrar sesion
 * tiene que evitar.
 *
 * Tambien da acceso al cambio de contrasena, que hasta ahora solo se alcanzaba
 * cuando el servidor te obligaba a pasar por el.
 */
export function UserMenu() {
  const user = useCurrentUser();
  const signOut = useSessionStore((estado) => estado.signOut);
  const [abierto, setAbierto] = useState(false);
  const [saliendo, setSaliendo] = useState(false);
  const menuId = useId();

  if (user === null) return null;

  const salir = async (todosLosDispositivos: boolean): Promise<void> => {
    setSaliendo(true);

    try {
      await signOut(todosLosDispositivos);
    } catch {
      // El estado local ya quedo limpio dentro del store pase lo que pase, de
      // modo que la guarda de rutas llevara al acceso igual. Se traga el error
      // a proposito: avisar de que "no se pudo cerrar sesion" a quien ya esta
      // fuera de la aplicacion solo confunde.
    } finally {
      setAbierto(false);
      setSaliendo(false);
    }
  };

  return (
    <div
      className="relative"
      onBlur={(event) => {
        // Solo cierra si el foco sale del componente entero: moverse entre el
        // disparador y las opciones con el teclado no debe cerrarlo.
        if (!event.currentTarget.contains(event.relatedTarget)) setAbierto(false);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        setAbierto(false);
        event.currentTarget.querySelector('button')?.focus();
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={abierto ? menuId : undefined}
        // El nombre visible se queda dentro del accesible -"Sesion: Ana
        // Quispe" contiene "Ana Quispe"-, que es lo que exige el criterio de
        // etiqueta en el nombre. En movil el nombre se oculta y sin esto el
        // boton se anunciaria sin nombre ninguno.
        aria-label={`Sesion: ${user.fullName}`}
        onClick={() => {
          setAbierto((previo) => !previo);
        }}
        className={cn(
          'flex h-9 items-center gap-2.5 rounded-md border border-line-strong bg-panel pl-1.5 pr-2',
          'text-sm text-fg-body transition-colors duration-(--t-tap)',
          'hover:border-n300 focus-visible:border-brand',
        )}
      >
        <Iniciales nombre={user.fullName} />
        <span className="hidden max-w-52 truncate font-medium lg:inline">{user.fullName}</span>
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-fg-subtle transition-transform duration-(--t-tap)',
            abierto && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {abierto && (
        <div
          id={menuId}
          role="menu"
          aria-label="Sesion"
          className={cn(
            'absolute right-0 top-full z-20 mt-1.5 w-72 overflow-hidden rounded-md',
            'border border-line-strong bg-panel shadow-[0_8px_24px_-6px_rgb(28_26_24/0.18)]',
          )}
        >
          <div className="border-b border-line px-3 py-2.5">
            <p className="truncate text-sm font-medium text-fg">{user.fullName}</p>
            <p className="truncate text-xs text-fg-subtle">{user.email}</p>
          </div>

          <div className="p-1">
            <Link
              to="/cambiar-contrasena"
              role="menuitem"
              onClick={() => {
                setAbierto(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-fg-body',
                'transition-colors duration-(--t-tap) hover:bg-brand-tint hover:text-brand-ink',
              )}
            >
              <KeyRound className="size-3.5 shrink-0" aria-hidden="true" />
              Cambiar contrasena
            </Link>
          </div>

          <div className="border-t border-line p-1">
            <OpcionSalir
              icono={<LogOut className="size-3.5 shrink-0" aria-hidden="true" />}
              etiqueta="Cerrar sesion"
              deshabilitada={saliendo}
              onSelect={() => void salir(false)}
            />
            <OpcionSalir
              icono={<MonitorSmartphone className="size-3.5 shrink-0" aria-hidden="true" />}
              etiqueta="Cerrar en todos los dispositivos"
              deshabilitada={saliendo}
              onSelect={() => void salir(true)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function OpcionSalir({
  icono,
  etiqueta,
  deshabilitada,
  onSelect,
}: {
  readonly icono: React.ReactNode;
  readonly etiqueta: string;
  readonly deshabilitada: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={deshabilitada}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded-xs px-2 py-1.5 text-left text-sm text-fg-body',
        'transition-colors duration-(--t-tap)',
        'hover:bg-brand-tint hover:text-brand-ink disabled:opacity-50',
      )}
    >
      {icono}
      {etiqueta}
    </button>
  );
}

/**
 * Iniciales en lugar de una fotografia.
 *
 * El sistema no guarda imagenes de las personas y no va a empezar a hacerlo
 * para adornar una barra. Dos letras bastan para reconocer de un vistazo con
 * que cuenta se esta trabajando, que es justo lo que hace falta cuando se
 * alterna entre perfiles para comprobar permisos.
 */
function Iniciales({ nombre }: { readonly nombre: string }) {
  const partes = nombre
    .trim()
    .split(/\s+/u)
    .filter((parte) => parte.length > 0);
  const iniciales = [partes[0], partes[1]]
    .map((parte) => parte?.charAt(0).toUpperCase() ?? '')
    .join('');

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-sm',
        'bg-brand-tint text-xs font-semibold text-brand-ink',
      )}
    >
      {iniciales === '' ? '?' : iniciales}
    </span>
  );
}
